import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { Guid, once } from '@web/utils';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
  ToastService,
} from '@web/shared/ui';
import { SummernoteImageUpload } from '@web/shared/external';
import { AdminBoxSaleDataSourceService } from '../../services/admin-box-sale-data-source.service';
import { AdminBoxSaleDetailsForm } from '../../models/admin-box-sale-details-form';
import { AdminBoxSaleDetailsFormComponent } from '../admin-box-sale-details-form/admin-box-sale-details-form.component';

@Component({
  selector: 'app-admin-box-sale-edit',
  imports: [
    AdminBoxSaleDetailsFormComponent,
    AlertsComponent,
    CommonModule,
    SpinnerFilledComponent,
  ],
  templateUrl: './admin-box-sale-edit.component.html',
  styleUrl: './admin-box-sale-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminBoxSaleEditComponent {
  private readonly dataSource = inject(AdminBoxSaleDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);
  private readonly toast = inject(ToastService);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output<void>();

  protected readonly id$ = toObservable(this.id);
  protected readonly form$ = this.formSource();
  protected readonly uploadFn = (
    files: FileList,
  ): Observable<SummernoteImageUpload[]> =>
    this.dataSource.uploadEditorImages(Array.from(files));

  public handleSubmitClickEvent(data: AdminBoxSaleDetailsForm): void {
    this.dataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => this.submitSuccessEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.showFailureToast();
    this.showFailureAlerts(messages);
  }

  private showFailureToast(): void {
    this.toast.show((builder) =>
      builder
        .initError(
          'Wystąpił błąd podczas zapisu. Sprawdź poprawność wprowadzonych danych.',
        )
        .build(),
    );
  }

  private showFailureAlerts(messages: string[]): void {
    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private formSource(): Observable<AdminBoxSaleDetailsForm> {
    return this.id$.pipe(
      switchMap((id) => this.dataSource.fetchDetailsFormById(id)),
    );
  }
}
